import { useMemo, useRef, useState } from 'react'
import { useForm, usePage } from '@inertiajs/react'
import { Info, Plus } from 'lucide-react'
import {
  DataFieldTypeAndFlags,
  DataFieldValidationEditor,
  type DataFieldTypeAndFlagsLabels,
  type DataFieldValidationEditorLabels,
} from '@/features/data-field-config'
import {
  applyBetweenPrimaryOnAdd,
  applyDataFieldTypeDefaults,
  DATA_FIELD_TEXT_MAX_LENGTH,
  defaultIsAcceptableForField,
  defaultValidationTypeIdForField,
  hasContradictoryAcceptableConditions,
  isBetweenValidationType,
  isDefinedValueType,
  resolveFieldTypeCode,
  shouldDisableValidateField,
  shouldShowValidateField,
  validationOptionsForField,
} from '@/shared/lib/data-field-config'
import { isValidBetweenBounds, parseBetweenBounds } from '@/shared/lib/data-field-bounds'
import { Button } from '@/shared/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import TenantTimeInput from '@/shared/ui/tenant-time-input'
import {
  calendarStartsAtBlocksSubmit,
  isExistingCalendarStartsAtMissing,
  isNewIntervalCalendarStartsAtMissing,
  resolveCalendarFixedScheduleStartTime,
  resolveInitialFixedScheduleStartTime,
  resolveStartTimeOnIntervalSelect,
} from '@/pages/Admin/InstrumentTypes/maintenance-task-starts-at-gates'
import {
  isDuplicateIntervalName,
  resolveStickyIntervalName,
  seedNewIntervalAutoName,
} from '@/pages/Admin/InstrumentTypes/maintenance-task-new-interval-name'
import {
  resolveEndsAtMidnightForPayload,
  resolveEndsAtMidnightOnIntervalSelect,
} from '@/pages/Admin/InstrumentTypes/maintenance-task-ends-at-midnight'
import { Checkbox } from '@/shared/ui/checkbox'
import { RichTextEditor } from '@/shared/ui/rich-text-editor'
import { RruleBuilder } from '@/shared/ui/rrule-builder'
import { endsAtMidnightEligible } from '@/shared/lib/endsAtMidnightEligible'
import { formatSiteDateTime, toWallClockString } from '@/shared/lib/tenantDateTime'
import { humanReadableStructuralRrule } from '@/shared/lib/frequency/rrule-utils'
import {
  shortestBydayGapMinutes,
  shortestMonthlyGapMinutes,
} from '@/shared/lib/frequency/shortest-byday-gap-minutes'
import { isRenderablePreviewDate } from '@/shared/lib/frequency/overdue-preview'
import {
  formatIntervalOptionLabel,
  convertFromMinutes,
  buildCalendarAutoName,
  buildTimedAutoName,
} from '@/shared/lib/frequency/format-interval-option-label'
import {
  buildTimedIntervalSchedule,
  MAX_OVERDUE_VALUE,
  overdueAboveMaximum,
  overdueBelowMinimum,
  overdueDurationTooLong,
} from '@/pages/Admin/InstrumentTypes/maintenance-task-timed-interval'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { TouchTooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { SelectCombobox } from '@/shared/ui/select-combobox'
import { t, type TranslationKey } from '@/shared/lib/i18n'
import {
  adminInstrumentTypeMaintenanceTasksPath,
  adminInstrumentTypeMaintenanceTaskPath,
} from '@/shared/lib/routes'
import type {
  WebAdminInstrumentTypeMaintenanceTaskRow,
  WebFrequencyType,
  WebInstrumentTaskIntervalType,
  WebInstrumentsTaskDataFieldTypeOption,
  WebInstrumentsTaskDataFieldValidationTypeOption,
} from '@/shared/types/web'

const FREQUENCY_TYPE_TIMED = 'TIMED'
const FREQUENCY_TYPE_CALENDAR_FIXED = 'CALENDAR_FIXED'
const FREQUENCY_TYPE_UNSCHEDULED = 'UNSCHEDULED'
const NEW_INTERVAL_VALUE = '__new__'

const TIME_UNIT_TO_MINUTES: Record<string, number> = {
  minutes: 1,
  hours: 60,
  days: 1440,
  weeks: 10080,
  months: 43200,
  years: 525600,
}

// Maps an RRULE FREQ to the matching TIME_UNIT_TO_MINUTES key (legacy freqToUnitMap),
// so a calendar-fixed "Repeats Every" cadence can be compared against the Overdue duration
// the same way a Timed frequency's repeatsEveryValue/Unit is.
const RRULE_FREQ_TO_UNIT: Record<string, string> = {
  MINUTELY: 'minutes',
  HOURLY: 'hours',
  DAILY: 'days',
  WEEKLY: 'weeks',
  MONTHLY: 'months',
  YEARLY: 'years',
}

const TIME_UNITS: { value: string; labelKey: TranslationKey }[] = [
  { value: 'minutes', labelKey: 'instruments.tasksSchedule.form.timeUnits.minutes' },
  { value: 'hours', labelKey: 'instruments.tasksSchedule.form.timeUnits.hours' },
  { value: 'days', labelKey: 'instruments.tasksSchedule.form.timeUnits.days' },
  { value: 'weeks', labelKey: 'instruments.tasksSchedule.form.timeUnits.weeks' },
  { value: 'months', labelKey: 'instruments.tasksSchedule.form.timeUnits.months' },
  { value: 'years', labelKey: 'instruments.tasksSchedule.form.timeUnits.years' },
]

const toMinutes = (value: string, unit: string): number =>
  Math.round(Number(value) * (TIME_UNIT_TO_MINUTES[unit] ?? 1))

export interface LotOption {
  id: number
  label: string
}

interface AcceptableValueRow {
  id?: string
  _destroy?: boolean
  value: string
  isAcceptable: boolean
  operatorType?: 'OR' | 'AND' | ''
  active: boolean
  instrumentTaskDataFieldValidationTypeId: string
}

interface DefinedValueRow {
  id?: string
  _destroy?: boolean
  value: string
  isAcceptable: boolean
}

interface DataFieldRow {
  id?: string
  label: string
  dataFieldDescription: string
  instrumentTaskDataFieldTypeId: string
  required: boolean
  requiresValidation: boolean
  isConfidential: boolean
  active: boolean
  acceptableValues: AcceptableValueRow[]
  definedValues: DefinedValueRow[]
}

interface NewIntervalDraft {
  name: string
  repeatsEveryValue: string // TIMED only
  repeatsEveryUnit: string // TIMED only
  // Calendar Fixed: legacy parity — never merged into the RRULE; doubles as this task's
  // own initial fixedScheduleStartTime (see MaintenanceFormData doc comment) only.
  startsAt: string
  frequencyRrule: string // Calendar Fixed: structural RRULE (freq/interval/day rules, no time)
  leadValue: string
  leadUnit: string
  overdueValue: string
  overdueUnit: string
}

interface MaintenanceFormData {
  name: string
  notes: string
  affectsCompliance: boolean
  frequencyTypeCode: string
  intervalTypeId: string
  newInterval: NewIntervalDraft
  // Calendar Fixed only. Legacy parity: fixedScheduleStartTime is a per-task column (never
  // baked into the shared interval's frequency_rrule), so it stays independently editable
  // for an EXISTING interval too, unlike Repeats Every/Overdue which are shared across every
  // task on that interval. Picking an interval never pre-fills this — the user always
  // confirms it, exactly like legacy's frequency picker.
  fixedScheduleStartTime: string
  lotId: string
  usedLotIds: number[]
  dataFields: DataFieldRow[]
  endsAtMidnight: boolean
}

const LOT_LINK_NONE_VALUE = '__none__'

const DEFAULT_CALENDAR_RRULE = 'FREQ=WEEKLY;INTERVAL=1'

/** Fresh New Frequency draft; Name matches the auto placeholder for the active frequency type. */
const emptyNewInterval = (frequencyTypeCode = FREQUENCY_TYPE_CALENDAR_FIXED): NewIntervalDraft => {
  const draft: NewIntervalDraft = {
    name: '',
    repeatsEveryValue: '',
    repeatsEveryUnit: 'days',
    startsAt: '00:00',
    frequencyRrule: DEFAULT_CALENDAR_RRULE,
    leadValue: '',
    leadUnit: 'hours',
    overdueValue: '',
    overdueUnit: 'hours',
  }
  draft.name = seedNewIntervalAutoName({
    frequencyTypeCode,
    repeatsEveryValue: draft.repeatsEveryValue,
    repeatsEveryUnit: draft.repeatsEveryUnit,
    frequencyRrule: draft.frequencyRrule,
  })
  return draft
}

const emptyDataField = (): DataFieldRow => ({
  label: '',
  dataFieldDescription: '',
  instrumentTaskDataFieldTypeId: '',
  required: true,
  requiresValidation: false,
  isConfidential: false,
  active: true,
  acceptableValues: [],
  definedValues: [],
})

const buildInitial = (
  editingRow: WebAdminInstrumentTypeMaintenanceTaskRow | null,
  stripIds = false,
): MaintenanceFormData => {
  if (!editingRow) {
    return {
      name: '',
      notes: '',
      affectsCompliance: true,
      frequencyTypeCode: '',
      intervalTypeId: '',
      newInterval: emptyNewInterval(),
      // Legacy parity: the Starts At control always defaults to midnight, never truly blank
      // (frequencies-list.component.ts sets startTime: ['00:00'] on the form) — only a task's
      // own existing value overrides that default.
      fixedScheduleStartTime: '00:00',
      lotId: '',
      usedLotIds: [],
      dataFields: [],
      endsAtMidnight: true,
    }
  }

  return {
    name: stripIds ? `${editingRow.name ?? ''} (Copy)` : (editingRow.name ?? ''),
    notes: editingRow.notes ?? '',
    affectsCompliance: editingRow.affectsCompliance,
    frequencyTypeCode: editingRow.frequencyTypeCode ?? '',
    intervalTypeId:
      editingRow.instrumentTaskIntervalTypeId != null
        ? String(editingRow.instrumentTaskIntervalTypeId)
        : '',
    newInterval: emptyNewInterval(),
    // Edit and clone both keep the source task's own start time (REBUILD-621); midnight is only
    // the default when the source has none. Picking an interval still never overwrites it.
    fixedScheduleStartTime: resolveInitialFixedScheduleStartTime(editingRow.fixedScheduleStartTime),
    lotId: editingRow.lotId != null ? String(editingRow.lotId) : '',
    usedLotIds: editingRow.usedLotIds,
    endsAtMidnight: editingRow.endsAtMidnight,
    dataFields: editingRow.dataFields.map((field) => ({
      id: stripIds ? undefined : String(field.id),
      label: field.label ?? '',
      dataFieldDescription: field.dataFieldDescription ?? '',
      instrumentTaskDataFieldTypeId:
        field.instrumentTaskDataFieldTypeId != null
          ? String(field.instrumentTaskDataFieldTypeId)
          : '',
      required: field.required,
      requiresValidation: field.requiresValidation,
      isConfidential: field.isConfidential,
      active: field.active,
      acceptableValues: (
        field.acceptableValueRows as {
          id: number
          value: string | null
          isAcceptable: boolean
          active: boolean
          operatorType?: 'OR' | 'AND' | ''
          instrumentTaskDataFieldValidationTypeId: number
        }[]
      ).map((acceptableRow) => ({
        id: stripIds ? undefined : String(acceptableRow.id),
        value: acceptableRow.value ?? '',
        isAcceptable: acceptableRow.isAcceptable,
        operatorType: acceptableRow.operatorType ?? '',
        active: acceptableRow.active,
        instrumentTaskDataFieldValidationTypeId: String(
          acceptableRow.instrumentTaskDataFieldValidationTypeId,
        ),
      })),
      definedValues: (field.definedValueRows as { id: number; value: string }[]).map(
        (definedRow) => {
          const matchingRule = (
            field.acceptableValueRows as {
              id: number
              value: string | null
              isAcceptable: boolean
              active: boolean
              instrumentTaskDataFieldValidationTypeId: number
              instrumentTaskDefinedValueId: number | null
            }[]
          ).find(
            (rule) =>
              (rule.instrumentTaskDefinedValueId != null &&
                rule.instrumentTaskDefinedValueId === definedRow.id) ||
              (rule.instrumentTaskDefinedValueId == null &&
                rule.value != null &&
                rule.value.trim().toLowerCase() === definedRow.value.trim().toLowerCase()),
          )
          return {
            id: stripIds ? undefined : String(definedRow.id),
            value: definedRow.value,
            isAcceptable: matchingRule ? matchingRule.isAcceptable : false,
          }
        },
      ),
    })),
  }
}

/** Nested JSON values safe for Inertia `router.post` / `FormDataConvertible`. */
type InertiaJson =
  | string
  | number
  | boolean
  | null
  | undefined
  | InertiaJson[]
  | { [key: string]: InertiaJson }

export type MaintenanceTaskPersistPayload = {
  maintenanceTask: { [key: string]: InertiaJson }
}

const MaintenanceTaskModalForm = ({
  typeId,
  editingRow,
  cloneFromRow,
  frequencyTypeOptions,
  intervalTypeOptions,
  dataFieldTypeOptions,
  dataFieldValidationTypeOptions,
  lotOptions,
  onClose,
  draftMode = false,
  onLocalSave,
}: {
  typeId: number | null
  editingRow: WebAdminInstrumentTypeMaintenanceTaskRow | null
  cloneFromRow: WebAdminInstrumentTypeMaintenanceTaskRow | null
  frequencyTypeOptions: WebFrequencyType[]
  intervalTypeOptions: WebInstrumentTaskIntervalType[]
  dataFieldTypeOptions: WebInstrumentsTaskDataFieldTypeOption[]
  dataFieldValidationTypeOptions: WebInstrumentsTaskDataFieldValidationTypeOption[]
  lotOptions: LotOption[]
  onClose: () => void
  draftMode?: boolean
  onLocalSave?: (
    payload: MaintenanceTaskPersistPayload,
    displayRow: WebAdminInstrumentTypeMaintenanceTaskRow,
  ) => void
}) => {
  const isEdit = editingRow !== null
  const isClone = !isEdit && cloneFromRow !== null
  const sourceRow = isEdit ? editingRow : cloneFromRow
  const form = useForm<MaintenanceFormData>(buildInitial(sourceRow, isClone))
  const { tenantTimeFormat, tenantTimeZone } = usePage<{
    tenantTimeFormat?: number | null
    tenantTimeZone?: string | null
  }>().props
  // Scoped to this modal's own submit attempts via the form's onError callback below — not
  // usePage().props.errors, which is a page-wide store that outlives this component (it still
  // held a previous, unrelated failure the next time any row's modal was opened, since opening
  // it is a client-side state change, not a fresh page visit that would overwrite those props).
  const [submitErrors, setSubmitErrors] = useState<Record<string, string | string[]>>({})
  // This modal has no per-field error slots for the inline "create new frequency" sub-form
  // (e.g. interval_type_description, frequency_rrule), so every backend validation message —
  // not just :base — is surfaced in the single banner below; otherwise a rejected save (like a
  // duplicate-frequency uniqueness error) would fail with no visible feedback. The backend sends
  // one or more messages per field (Rails errors.to_hash values are arrays) even though Inertia's
  // own onError type is a single string per key, so both shapes are normalized here.
  const baseErrors = Object.values(submitErrors).flatMap((message) =>
    Array.isArray(message) ? message : [message],
  )
  const [touched, setTouched] = useState<Partial<Record<keyof NewIntervalDraft, boolean>>>({})
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)

  const { frequencyTypeCode, intervalTypeId, newInterval } = form.data
  const isCreatingInterval = intervalTypeId === NEW_INTERVAL_VALUE
  const showIntervalOptions = isCreatingInterval && frequencyTypeCode !== FREQUENCY_TYPE_UNSCHEDULED
  const showLeadTime = frequencyTypeCode === FREQUENCY_TYPE_TIMED
  const showStartsAt = frequencyTypeCode === FREQUENCY_TYPE_CALENDAR_FIXED

  const matchingIntervals = useMemo(
    () =>
      intervalTypeOptions.filter((interval) => interval.frequencyTypeCode === frequencyTypeCode),
    [intervalTypeOptions, frequencyTypeCode],
  )

  const selectedExistingInterval = useMemo(() => {
    if (!intervalTypeId || intervalTypeId === NEW_INTERVAL_VALUE) return null
    return intervalTypeOptions.find((interval) => String(interval.id) === intervalTypeId) ?? null
  }, [intervalTypeOptions, intervalTypeId])

  // Mirrors formatIntervalOptionLabel — shows how this draft will appear in the
  // Frequency dropdown after saving, including Lead/Overdue suffixes.
  const intervalPreviewLabel = useMemo(() => {
    if (!showIntervalOptions || !newInterval.name) return ''
    let label = newInterval.name
    if (frequencyTypeCode === FREQUENCY_TYPE_CALENDAR_FIXED) {
      if (newInterval.overdueValue !== '' && Number(newInterval.overdueValue) > 0) {
        label += `, Overdue ${newInterval.overdueValue} ${newInterval.overdueUnit}`
      }
    } else if (frequencyTypeCode === FREQUENCY_TYPE_TIMED) {
      if (newInterval.leadValue !== '' && Number(newInterval.leadValue) > 0) {
        label += `, Lead ${newInterval.leadValue} ${newInterval.leadUnit}`
      }
      if (newInterval.overdueValue !== '' && Number(newInterval.overdueValue) > 0) {
        label += `, Overdue ${newInterval.overdueValue} ${newInterval.overdueUnit}`
      }
    }
    return label
  }, [showIntervalOptions, frequencyTypeCode, newInterval])

  // Whether the "Ends at 23:59:59" checkbox should be shown. Requires CALENDAR_FIXED type and
  // start+overdue that reaches the next site-local day; cadence length does not matter (REBUILD-263).
  // `lateTimeIntervalHours` is stored in minutes despite its name (see InstrumentTaskIntervalType).
  const relevantStartsAt = isCreatingInterval
    ? newInterval.startsAt
    : form.data.fixedScheduleStartTime
  const relevantOverdueMinutes = isCreatingInterval
    ? newInterval.overdueValue !== ''
      ? toMinutes(newInterval.overdueValue, newInterval.overdueUnit)
      : null
    : (selectedExistingInterval?.lateTimeIntervalHours ?? null)
  const showEndsAtMidnight = useMemo(() => {
    if (frequencyTypeCode !== FREQUENCY_TYPE_CALENDAR_FIXED) return false
    if (!relevantStartsAt || relevantOverdueMinutes == null) return false

    return endsAtMidnightEligible({
      startsAt: relevantStartsAt,
      overdueMinutes: relevantOverdueMinutes,
    })
  }, [frequencyTypeCode, relevantStartsAt, relevantOverdueMinutes])

  // Matrix RT15: lead must be strictly less than the interval (equal blocks).
  const leadExceedsRepeats = useMemo(() => {
    if (
      !showIntervalOptions ||
      !showLeadTime ||
      newInterval.repeatsEveryValue === '' ||
      newInterval.leadValue === ''
    )
      return false
    return (
      toMinutes(newInterval.leadValue, newInterval.leadUnit) >=
      toMinutes(newInterval.repeatsEveryValue, newInterval.repeatsEveryUnit)
    )
  }, [showIntervalOptions, showLeadTime, newInterval])

  // Overdue must not exceed the recurrence cadence (Timed) or shortest BYDAY gap
  // (Calendar multi-day — matrix RC13 / Assum #4). Equal to the limit is allowed.
  const overdueExceedsRepeats = useMemo(() => {
    if (!showIntervalOptions || newInterval.overdueValue === '') return false

    let repeatsEveryMinutes: number | null = null
    if (frequencyTypeCode === FREQUENCY_TYPE_TIMED) {
      if (newInterval.repeatsEveryValue === '') return false
      repeatsEveryMinutes = toMinutes(newInterval.repeatsEveryValue, newInterval.repeatsEveryUnit)
    } else if (frequencyTypeCode === FREQUENCY_TYPE_CALENDAR_FIXED) {
      const shortestGapMinutes =
        shortestBydayGapMinutes(newInterval.frequencyRrule) ??
        shortestMonthlyGapMinutes(newInterval.frequencyRrule)
      if (shortestGapMinutes != null) {
        repeatsEveryMinutes = shortestGapMinutes
      } else {
        const freqMatch = newInterval.frequencyRrule.match(/FREQ=([A-Z]+)/i)
        const intervalMatch = newInterval.frequencyRrule.match(/INTERVAL=(\d+)/i)
        const unit = RRULE_FREQ_TO_UNIT[freqMatch?.[1]?.toUpperCase() ?? '']
        if (!unit) return false
        const rruleInterval = intervalMatch ? parseInt(intervalMatch[1], 10) : 1
        repeatsEveryMinutes = TIME_UNIT_TO_MINUTES[unit] * rruleInterval
      }
    }

    if (repeatsEveryMinutes == null) return false
    return toMinutes(newInterval.overdueValue, newInterval.overdueUnit) > repeatsEveryMinutes
  }, [showIntervalOptions, frequencyTypeCode, newInterval])

  // Timed frequencies have no calendar anchor (unlike Calendar Fixed's Starts At) — each
  // execution is scheduled relative to the previous one, so the "if completed now" preview
  // is anchored on the current moment (legacy frequencies-list.component's previewDate when
  // no startTime is set).
  const timedNextExecution = useMemo(() => {
    if (!showLeadTime || newInterval.repeatsEveryValue === '') return null
    const repeatsEveryMinutes = toMinutes(
      newInterval.repeatsEveryValue,
      newInterval.repeatsEveryUnit,
    )
    if (repeatsEveryMinutes <= 0) return null
    const nextExecution = new Date(new Date().getTime() + repeatsEveryMinutes * 60 * 1000)
    // A huge Repeats Every yields an Invalid Date that would crash the preview render.
    return isRenderablePreviewDate(nextExecution) ? nextExecution : null
  }, [showLeadTime, newInterval.repeatsEveryValue, newInterval.repeatsEveryUnit])

  const timedOverdueDate = useMemo(() => {
    if (!timedNextExecution || newInterval.overdueValue === '') return null
    const overdueMinutes = toMinutes(newInterval.overdueValue, newInterval.overdueUnit)
    const overdueDate = new Date(timedNextExecution.getTime() + overdueMinutes * 60 * 1000)
    // A huge Overdue yields an Invalid Date: hide the preview instead of crashing (REBUILD-629).
    return isRenderablePreviewDate(overdueDate) ? overdueDate : null
  }, [timedNextExecution, newInterval.overdueValue, newInterval.overdueUnit])

  const linkedLotId = form.data.lotId ? Number(form.data.lotId) : null

  // Task Links dropdown: all lots are always available (legacy behavior).
  const linkFilteredLotOptions = lotOptions

  // Task Uses Lots checkboxes: hide the lot currently chosen in Task Links.
  const usesFilteredLotOptions = useMemo(
    () => lotOptions.filter((lot) => lot.id !== linkedLotId),
    [lotOptions, linkedLotId],
  )

  const buildAutoName = (draft: NewIntervalDraft): string => {
    if (frequencyTypeCode === FREQUENCY_TYPE_CALENDAR_FIXED) {
      return buildCalendarAutoName({ frequencyRrule: draft.frequencyRrule })
    }
    return buildTimedAutoName({
      repeatsEveryValue: draft.repeatsEveryValue,
      repeatsEveryUnit: draft.repeatsEveryUnit,
    })
  }

  const REPEATS_EVERY_KEYS: (keyof NewIntervalDraft)[] = [
    'repeatsEveryValue',
    'repeatsEveryUnit',
    'frequencyRrule',
  ]

  const setNewInterval = (patch: Partial<NewIntervalDraft>) => {
    const current = form.data.newInterval
    const next = { ...current, ...patch }
    const recurrenceChanged = REPEATS_EVERY_KEYS.some((key) => key in patch)
    next.name = resolveStickyIntervalName({
      currentName: current.name,
      previousAutoName: buildAutoName(current),
      nextAutoName: buildAutoName(next),
      recurrenceChanged,
    })
    form.setData('newInterval', next)
  }

  const onFrequencyTypeChange = (code: string) => {
    form.setData((previous) => ({
      ...previous,
      frequencyTypeCode: code,
      intervalTypeId: '',
      // Re-seed so Timed does not keep a Calendar "every week" placeholder (REBUILD-440 B1).
      newInterval: emptyNewInterval(code),
    }))
  }

  // Data field helpers ------------------------------------------------------

  const patchDataField = (fieldIndex: number, patch: Partial<DataFieldRow>) => {
    form.setData(
      'dataFields',
      form.data.dataFields.map((field, index) =>
        index === fieldIndex ? { ...field, ...patch } : field,
      ),
    )
  }

  const addDataField = () => {
    form.setData('dataFields', [...form.data.dataFields, emptyDataField()])
  }

  const cloneDataField = (fieldIndex: number) => {
    const source = form.data.dataFields[fieldIndex]
    const clone: DataFieldRow = {
      ...source,
      id: undefined,
      label: source.label,
      acceptableValues: source.acceptableValues.map((row) => ({ ...row, id: undefined })),
      definedValues: source.definedValues.map((row) => ({ ...row, id: undefined })),
    }
    const next = [...form.data.dataFields]
    next.splice(fieldIndex + 1, 0, clone)
    form.setData('dataFields', next)
  }

  // Legacy removeDataField: unsaved rows are spliced out; saved rows stay in the payload
  // with active=false so the backend soft-deactivates (REBUILD-538) instead of hard-deleting.
  const removeDataField = (fieldIndex: number) => {
    const field = form.data.dataFields[fieldIndex]
    if (!field) return

    if (field.id == null || field.id === '') {
      form.setData(
        'dataFields',
        form.data.dataFields.filter((_, index) => index !== fieldIndex),
      )
      return
    }

    form.setData(
      'dataFields',
      form.data.dataFields.map((row, index) =>
        index === fieldIndex ? { ...row, active: false } : row,
      ),
    )
  }

  const patchAcceptable = (
    fieldIndex: number,
    rowIndex: number,
    patch: Partial<AcceptableValueRow>,
  ) => {
    const field = form.data.dataFields[fieldIndex]
    const nextRows = field.acceptableValues.map((row, index) =>
      index === rowIndex ? { ...row, ...patch } : row,
    )
    // Switching a row to "Between" makes it the single primary condition (legacy
    // single-Between semantics, REBUILD-515): collapse the others away.
    const switchingToBetween =
      patch.instrumentTaskDataFieldValidationTypeId !== undefined &&
      isBetweenValidationType(
        patch.instrumentTaskDataFieldValidationTypeId,
        dataFieldValidationTypeOptions,
      )
    patchDataField(fieldIndex, {
      acceptableValues: switchingToBetween
        ? applyBetweenPrimaryOnAdd(nextRows, dataFieldValidationTypeOptions)
        : nextRows,
    })
  }

  const addAcceptable = (fieldIndex: number) => {
    const field = form.data.dataFields[fieldIndex]
    const typeCode = resolveFieldTypeCode(field.instrumentTaskDataFieldTypeId, dataFieldTypeOptions)
    const newRow: AcceptableValueRow = {
      value: '',
      isAcceptable: defaultIsAcceptableForField(typeCode),
      active: true,
      instrumentTaskDataFieldValidationTypeId: defaultValidationTypeIdForField(
        typeCode,
        dataFieldValidationTypeOptions,
      ),
    }
    const rowsWithNew = [...field.acceptableValues, newRow]
    // If the added row is a "Between" condition it becomes primary and clears the rest.
    patchDataField(fieldIndex, {
      acceptableValues: isBetweenValidationType(
        newRow.instrumentTaskDataFieldValidationTypeId,
        dataFieldValidationTypeOptions,
      )
        ? applyBetweenPrimaryOnAdd(rowsWithNew, dataFieldValidationTypeOptions)
        : rowsWithNew,
    })
  }

  const removeAcceptable = (fieldIndex: number, rowIndex: number) => {
    const field = form.data.dataFields[fieldIndex]
    patchDataField(fieldIndex, {
      acceptableValues: field.acceptableValues.filter((_, index) => index !== rowIndex),
    })
  }

  const patchDefined = (fieldIndex: number, rowIndex: number, value: string) => {
    const field = form.data.dataFields[fieldIndex]
    patchDataField(fieldIndex, {
      definedValues: field.definedValues.map((row, index) =>
        index === rowIndex ? { ...row, value } : row,
      ),
    })
  }

  const toggleDefinedAcceptable = (fieldIndex: number, rowIndex: number, isAcceptable: boolean) => {
    const field = form.data.dataFields[fieldIndex]
    patchDataField(fieldIndex, {
      definedValues: field.definedValues.map((row, index) =>
        index === rowIndex ? { ...row, isAcceptable } : row,
      ),
    })
  }

  const addDefined = (fieldIndex: number) => {
    const field = form.data.dataFields[fieldIndex]
    patchDataField(fieldIndex, {
      definedValues: [...field.definedValues, { value: '', isAcceptable: false }],
    })
  }

  const removeDefined = (fieldIndex: number, rowIndex: number) => {
    const field = form.data.dataFields[fieldIndex]
    patchDataField(fieldIndex, {
      definedValues: field.definedValues.filter((_, index) => index !== rowIndex),
    })
  }

  // Submission --------------------------------------------------------------

  const resolveFrequencyTypeId = (): number | '' => {
    const match = frequencyTypeOptions.find((option) => option.code === frequencyTypeCode)
    return match ? match.id : ''
  }

  const buildNewIntervalPayload = () => {
    const frequencyTypeId = resolveFrequencyTypeId()
    const timedSchedule =
      frequencyTypeCode === FREQUENCY_TYPE_TIMED && newInterval.repeatsEveryValue !== ''
        ? buildTimedIntervalSchedule(
            toMinutes(newInterval.repeatsEveryValue, newInterval.repeatsEveryUnit),
          )
        : null
    return {
      intervalTypeDescription: newInterval.name.trim(),
      frequencyTypeId,
      intervalHours: timedSchedule?.intervalHours ?? '',
      fixedSchedule: frequencyTypeCode === FREQUENCY_TYPE_CALENDAR_FIXED,
      // frequencyRrule never carries a time component — Starts At lives on the task's own
      // fixedScheduleStartTime (see submit()) and, as a default, on defaultStartTime below.
      frequencyRrule:
        frequencyTypeCode === FREQUENCY_TYPE_CALENDAR_FIXED
          ? newInterval.frequencyRrule || 'FREQ=DAILY;INTERVAL=1'
          : (timedSchedule?.frequencyRrule ?? ''),
      // Lead Time is Timed-only (AC 1/6): never persist a lead time for Calendar (Ex Fixed),
      // even if a value lingers in form state from a prior Timed selection.
      leadTimeIntervalHours:
        showLeadTime && newInterval.leadValue !== ''
          ? toMinutes(newInterval.leadValue, newInterval.leadUnit)
          : '',
      lateTimeIntervalHours:
        newInterval.overdueValue !== ''
          ? toMinutes(newInterval.overdueValue, newInterval.overdueUnit)
          : '',
      // REBUILD-621: the frequency remembers its Starts At as a default for later tasks.
      defaultStartTime:
        frequencyTypeCode === FREQUENCY_TYPE_CALENDAR_FIXED ? newInterval.startsAt.trim() : '',
      leadTimeUnit: showLeadTime && newInterval.leadValue !== '' ? newInterval.leadUnit : null,
      lateTimeUnit: newInterval.overdueValue !== '' ? newInterval.overdueUnit : null,
      // REBUILD-592: persist checkbox on the frequency definition (hidden window → false).
      endsAtMidnight: resolveEndsAtMidnightForPayload({
        showEndsAtMidnight,
        endsAtMidnight: form.data.endsAtMidnight,
      }),
    }
  }

  const submit = () => {
    setSubmitAttempted(true)
    if (dataFieldsInvalid) return
    // Defense in depth: Save is also disabled via submitDisabled, but empty Calendar
    // Starts At must not reach the payload if submit is invoked another way.
    if (calendarStartsAtMissing) return

    const isUnscheduled = form.data.frequencyTypeCode === FREQUENCY_TYPE_UNSCHEDULED
    const usingNewInterval = form.data.intervalTypeId === NEW_INTERVAL_VALUE && !isUnscheduled
    const lotLinked = Boolean(form.data.lotId)
    const persistPayload = {
      maintenanceTask: {
        name: form.data.name.trim(),
        notes: form.data.notes,
        affectsCompliance: form.data.affectsCompliance,
        fixedSchedule: form.data.frequencyTypeCode === FREQUENCY_TYPE_CALENDAR_FIXED,
        endsAtMidnight: resolveEndsAtMidnightForPayload({
          showEndsAtMidnight,
          endsAtMidnight: form.data.endsAtMidnight,
        }),
        // Unscheduled: send frequencyTypeCode so backend resolves the singleton; omit intervalTypeId.
        ...(isUnscheduled ? { frequencyTypeCode: FREQUENCY_TYPE_UNSCHEDULED } : {}),
        intervalTypeId: isUnscheduled ? '' : usingNewInterval ? '' : form.data.intervalTypeId,
        ...(usingNewInterval ? { newInterval: buildNewIntervalPayload() } : {}),
        // Per-task Starts At (legacy fixedScheduleStartTime): a new interval's own startsAt
        // input doubles as this task's initial value; an existing interval keeps its own
        // independently-edited value (see MaintenanceFormData doc comment).
        // Gap 2: never remap cleared `''` → `00:00` in the PATCH payload.
        fixedScheduleStartTime: resolveCalendarFixedScheduleStartTime({
          isCalendarFixed: form.data.frequencyTypeCode === FREQUENCY_TYPE_CALENDAR_FIXED,
          usingNewInterval,
          newIntervalStartsAt: form.data.newInterval.startsAt,
          fixedScheduleStartTime: form.data.fixedScheduleStartTime,
        }),
        lotId: form.data.lotId,
        usedLotIds: form.data.usedLotIds,
        dataFields: form.data.dataFields.map((field) => {
          const fieldTypeCode = resolveFieldTypeCode(
            field.instrumentTaskDataFieldTypeId,
            dataFieldTypeOptions,
          )
          const definedType = isDefinedValueType(fieldTypeCode)
          return {
            id: field.id,
            label: field.label,
            dataFieldDescription: field.dataFieldDescription,
            instrumentTaskDataFieldTypeId: field.instrumentTaskDataFieldTypeId || undefined,
            required: field.required,
            requiresValidation: definedType ? true : field.requiresValidation,
            isConfidential: field.isConfidential,
            active: field.active,
            acceptableValues: definedType
              ? field.definedValues
                  .filter((dv) => !dv._destroy)
                  .map((dv) => ({
                    value: dv.value,
                    isAcceptable: dv.isAcceptable,
                    active: true,
                  }))
              : field.requiresValidation && !lotLinked
                ? field.acceptableValues.map((row) => ({
                    id: row.id,
                    _destroy: row._destroy,
                    value: row.value,
                    isAcceptable: row.isAcceptable,
                    operatorType: row.operatorType,
                    active: row.active,
                    instrumentTaskDataFieldValidationTypeId:
                      row.instrumentTaskDataFieldValidationTypeId || undefined,
                  }))
                : [],
            definedValues: definedType
              ? field.definedValues.map((row) => ({
                  id: row.id,
                  _destroy: row._destroy,
                  value: row.value,
                }))
              : field.requiresValidation
                ? field.definedValues.map((row) => ({
                    id: row.id,
                    _destroy: row._destroy,
                    value: row.value,
                  }))
                : [],
          }
        }),
      },
    }

    if (draftMode && onLocalSave) {
      const displayName = persistPayload.maintenanceTask.name
      const displayRow: WebAdminInstrumentTypeMaintenanceTaskRow = {
        id: editingRow?.id ?? -Date.now(),
        name: displayName,
        notes: form.data.notes || null,
        fixedSchedule: form.data.frequencyTypeCode === FREQUENCY_TYPE_CALENDAR_FIXED,
        affectsCompliance: form.data.affectsCompliance,
        endsAtMidnight: Boolean(persistPayload.maintenanceTask.endsAtMidnight),
        instrumentTaskIntervalTypeId:
          form.data.intervalTypeId && form.data.intervalTypeId !== NEW_INTERVAL_VALUE
            ? Number(form.data.intervalTypeId)
            : null,
        lotId: form.data.lotId ? Number(form.data.lotId) : null,
        taskOrder: editingRow?.taskOrder ?? null,
        isAllowEdit: true,
        openNonComplianceDepartmentNames: [],
        openNonComplianceCount: 0,
        lotLabel: null,
        frequencyTypeId: null,
        frequencyTypeCode: form.data.frequencyTypeCode || null,
        fixedScheduleStartTime:
          typeof persistPayload.maintenanceTask.fixedScheduleStartTime === 'string'
            ? persistPayload.maintenanceTask.fixedScheduleStartTime
            : null,
        usedLotIds: form.data.usedLotIds.map(Number).filter((id) => !Number.isNaN(id)),
        dataFields: [],
        tags: [],
      }
      onLocalSave(persistPayload, displayRow)
      form.reset()
      onClose()
      return
    }

    if (typeId === null) return

    form.transform(() => persistPayload)

    const onSuccess = () => {
      form.reset()
      onClose()
    }

    const onError = (errors: Record<string, string>) => setSubmitErrors(errors)

    if (isEdit) {
      form.patch(adminInstrumentTypeMaintenanceTaskPath(typeId, editingRow.id), {
        preserveScroll: true,
        onSuccess,
        onError,
      })
    } else {
      form.post(adminInstrumentTypeMaintenanceTasksPath(typeId), {
        preserveScroll: true,
        onSuccess,
        onError,
      })
    }
  }

  // A maintenance task must carry a schedule (assigned interval or fixed schedule),
  // otherwise it is a non-scheduled task and would be classified as a Controls/Reagents
  // requirement (E27-S21 AC1) — but with no lot it lands in the controls section and
  // cannot be edited. UNSCHEDULED auto-assigns a tenant-singleton interval server-side
  // (AC 3), so no UI selection is needed. Timed and Calendar (Ex Fixed) both require an
  // explicit interval selection.
  const willBeScheduled = frequencyTypeCode === FREQUENCY_TYPE_UNSCHEDULED || intervalTypeId !== ''

  // A new interval requires every visible scheduling field to be filled. Errors are only
  // surfaced once a field has been touched (or a save is attempted) so the panel does not
  // render red the moment it opens. Save stays disabled until the interval is complete.
  const intervalNameMissing = showIntervalOptions && newInterval.name.trim().length === 0
  // A frequency name must be unique per tenant regardless of schedule (REBUILD-347): reject a
  // new interval whose name matches any existing frequency option, case-insensitively. Mirrors
  // the server-side uniqueness validation so the user gets feedback before submitting.
  const intervalNameDuplicate =
    showIntervalOptions &&
    isDuplicateIntervalName(
      newInterval.name,
      intervalTypeOptions.map((interval) => interval.intervalTypeDescription),
    )
  // repeatsEveryMissing only applies to TIMED — Calendar Fixed uses RruleBuilder for scheduling.
  const repeatsEveryMissing =
    showIntervalOptions &&
    showLeadTime &&
    (newInterval.repeatsEveryValue.trim() === '' || Number(newInterval.repeatsEveryValue) <= 0)
  const calendarRruleMissing =
    showIntervalOptions && showStartsAt && newInterval.frequencyRrule.trim() === ''
  const startsAtMissing = isNewIntervalCalendarStartsAtMissing({
    showIntervalOptions,
    showStartsAt,
    startsAt: newInterval.startsAt,
  })
  const overdueMissing =
    showIntervalOptions &&
    (newInterval.overdueValue.trim() === '' || Number(newInterval.overdueValue) <= 0)
  const overdueTooShort =
    showIntervalOptions &&
    !overdueMissing &&
    overdueBelowMinimum(toMinutes(newInterval.overdueValue, newInterval.overdueUnit))
  const overdueTooLarge =
    showIntervalOptions && !overdueMissing && overdueAboveMaximum(Number(newInterval.overdueValue))
  const overdueDurationExceeded =
    showIntervalOptions &&
    !overdueMissing &&
    !overdueTooLarge &&
    overdueDurationTooLong(toMinutes(newInterval.overdueValue, newInterval.overdueUnit))
  const newIntervalInvalid =
    intervalNameMissing ||
    intervalNameDuplicate ||
    repeatsEveryMissing ||
    calendarRruleMissing ||
    startsAtMissing ||
    overdueMissing ||
    overdueTooShort ||
    overdueTooLarge ||
    overdueDurationExceeded
  // The per-task Starts At (see MaintenanceFormData.fixedScheduleStartTime doc comment) is
  // required whenever an EXISTING Calendar Fixed interval is selected — mirrors newInterval's
  // own startsAt requirement for a brand-new interval.
  const existingStartsAtMissing = isExistingCalendarStartsAtMissing({
    isCreatingInterval,
    showStartsAt,
    intervalTypeId,
    fixedScheduleStartTime: form.data.fixedScheduleStartTime,
  })
  const calendarStartsAtMissing = calendarStartsAtBlocksSubmit({
    existingStartsAtMissing,
    startsAtMissing,
  })

  const markTouched = (field: keyof NewIntervalDraft) =>
    setTouched((previous) => ({ ...previous, [field]: true }))

  // A data field with any content (type, defined or acceptable values) but no label is
  // silently dropped server-side (InstrumentTaskDataField requires a label). Flag it so
  // the user sees why the task will not save instead of losing the field on save.

  const dataFieldLabelMissing = (field: DataFieldRow): boolean => field.label.trim().length === 0

  // Field Type is mandatory once a data field has any content (legacy `required` select).
  const dataFieldTypeMissing = (field: DataFieldRow): boolean =>
    field.instrumentTaskDataFieldTypeId === ''

  // A Between rule with both bounds filled but min > max is invalid (QA item 8).
  const dataFieldBetweenInvalid = (field: DataFieldRow): boolean =>
    field.acceptableValues.some((row) => {
      if (row._destroy) return false
      if (
        !isBetweenValidationType(
          row.instrumentTaskDataFieldValidationTypeId,
          dataFieldValidationTypeOptions,
        )
      )
        return false
      const bounds = parseBetweenBounds(row.value)
      return !isValidBetweenBounds(bounds.min, bounds.max)
    })

  // Contradictory acceptable conditions (e.g. "> 16" AND "between 5 and 10") can never all
  // be satisfied, so the task could never be completed — block save (REBUILD-515, QA item).
  // Lot-linked fields take their acceptable rules from the lot form, so skip them here.
  const dataFieldContradictory = (field: DataFieldRow): boolean => {
    if (linkedLotId != null) return false
    return hasContradictoryAcceptableConditions(
      field.acceptableValues,
      dataFieldValidationTypeOptions,
    )
  }

  const anyDataFieldLabelMissing = form.data.dataFields.some(dataFieldLabelMissing)
  const anyDataFieldTypeMissing = form.data.dataFields.some(dataFieldTypeMissing)
  const anyDataFieldBetweenInvalid = form.data.dataFields.some(dataFieldBetweenInvalid)
  const anyDataFieldContradictory = form.data.dataFields.some(dataFieldContradictory)

  const anyDataFieldRuleValueMissing = form.data.dataFields.some((field) => {
    const typeCode = resolveFieldTypeCode(field.instrumentTaskDataFieldTypeId, dataFieldTypeOptions)
    if (isDefinedValueType(typeCode)) {
      // When linked to a lot, defined values are set in the lot form — skip validation here
      if (linkedLotId != null) return false
      return field.definedValues.some(
        (r) => !(r as { _destroy?: boolean })._destroy && r.value.trim() === '',
      )
    }
    if (field.requiresValidation) {
      // When linked to a lot, acceptable values are set in the lot form — skip validation here
      if (linkedLotId != null) return false
      return field.acceptableValues.some((r) => !r._destroy && r.value.trim() === '')
    }
    return false
  })

  const anyDefinedValueMissingAcceptable = form.data.dataFields.some((field) => {
    const typeCode = resolveFieldTypeCode(field.instrumentTaskDataFieldTypeId, dataFieldTypeOptions)
    if (!isDefinedValueType(typeCode)) return false
    if (linkedLotId != null) return false
    const activeRows = field.definedValues.filter((r) => !(r as { _destroy?: boolean })._destroy)
    if (activeRows.length === 0) return false
    return !activeRows.some((r) => r.isAcceptable)
  })

  // Data-field validation is enforced on submit (not while typing/selecting a type), so
  // the button stays enabled and the guard clause in `submit` surfaces the errors.
  const dataFieldsInvalid =
    anyDataFieldLabelMissing ||
    anyDataFieldTypeMissing ||
    anyDataFieldBetweenInvalid ||
    anyDataFieldContradictory ||
    anyDataFieldRuleValueMissing ||
    anyDefinedValueMissingAcceptable

  const submitDisabled =
    form.processing ||
    form.data.name.trim().length === 0 ||
    !willBeScheduled ||
    newIntervalInvalid ||
    calendarStartsAtMissing ||
    leadExceedsRepeats ||
    overdueExceedsRepeats

  return (
    <DialogContent
      className="sm:max-w-2xl"
      onOpenAutoFocus={(event) => {
        // Radix focuses the first focusable element by default (the Info tooltip
        // trigger), which makes the tooltip pop open on mount. Focus the name
        // input instead so the user can type right away and no tooltip shows.
        event.preventDefault()
        nameInputRef.current?.focus()
      }}
    >
      <DialogHeader>
        <DialogTitle>
          {isEdit
            ? t('admin.instrumentTypes.maintenanceModal.editTitle')
            : isClone
              ? t('admin.instrumentTypes.maintenanceModal.cloneTitle')
              : t('admin.instrumentTypes.maintenanceModal.addTitle')}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-5 max-h-[65vh] overflow-y-auto p-1 -m-1">
        {baseErrors.length > 0 && (
          <div className="rounded border border-destructive bg-destructive/10 px-3 py-2">
            {baseErrors.map((message, index) => (
              <p key={index} className="text-sm text-destructive">
                {message}
              </p>
            ))}
          </div>
        )}

        {/* Name + notes ------------------------------------------------ */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <Label htmlFor="maintenance-name">
              {t('admin.instrumentTypes.maintenanceModal.nameLabel')} *
            </Label>
            <TouchTooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={t('admin.instrumentTypes.maintenanceModal.nameInfoLabel')}
                >
                  <Info className="h-4 w-4" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {t('admin.instrumentTypes.maintenanceModal.nameExampleTooltip')}
              </TooltipContent>
            </TouchTooltip>
          </div>
          <Input
            id="maintenance-name"
            ref={nameInputRef}
            value={form.data.name}
            onChange={(event) => form.setData('name', event.target.value)}
            placeholder={t('admin.instrumentTypes.maintenanceModal.namePlaceholder')}
            autoComplete="off"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="maintenance-notes">
            {t('admin.instrumentTypes.maintenanceModal.notesLabel')}
          </Label>
          <RichTextEditor
            id="maintenance-notes"
            value={form.data.notes}
            onChange={(htmlValue) => form.setData('notes', htmlValue)}
            placeholder={t('admin.instrumentTypes.maintenanceModal.notesPlaceholder')}
          />
        </div>

        {/* Frequency --------------------------------------------------- */}
        <div className="space-y-3 rounded-md border p-3">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Label>{t('admin.instrumentTypes.maintenanceModal.frequencyTypeLabel')}</Label>
              <TouchTooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="text-muted-foreground hover:text-foreground">
                    <Info className="h-4 w-4" aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs space-y-1">
                  <p>
                    <strong>
                      {t('admin.instrumentTypes.maintenanceModal.frequencyTypeTimed')}:
                    </strong>{' '}
                    {t('admin.instrumentTypes.maintenanceModal.frequencyTypeTimedTooltip')}
                  </p>
                  <p>
                    <strong>
                      {t('admin.instrumentTypes.maintenanceModal.frequencyTypeCalendarFixed')}:
                    </strong>{' '}
                    {t('admin.instrumentTypes.maintenanceModal.frequencyTypeCalendarFixedTooltip')}
                  </p>
                </TooltipContent>
              </TouchTooltip>
            </div>
            <Select value={frequencyTypeCode || undefined} onValueChange={onFrequencyTypeChange}>
              <SelectTrigger>
                <SelectValue
                  placeholder={t('admin.instrumentTypes.maintenanceModal.frequencyTypePlaceholder')}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FREQUENCY_TYPE_TIMED}>
                  {t('admin.instrumentTypes.maintenanceModal.frequencyTypeTimed')}
                </SelectItem>
                <SelectItem value={FREQUENCY_TYPE_CALENDAR_FIXED}>
                  {t('admin.instrumentTypes.maintenanceModal.frequencyTypeCalendarFixed')}
                </SelectItem>
                <SelectItem value={FREQUENCY_TYPE_UNSCHEDULED}>
                  {t('admin.instrumentTypes.maintenanceModal.frequencyTypeUnscheduled')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {frequencyTypeCode === FREQUENCY_TYPE_UNSCHEDULED ? (
            <p className="text-sm text-muted-foreground">
              {t('admin.instrumentTypes.maintenanceModal.unscheduledNote')}
            </p>
          ) : (
            <div className="space-y-1.5">
              <Label>{t('admin.instrumentTypes.maintenanceModal.frequencyLabel')}</Label>
              <SelectCombobox
                disabled={frequencyTypeCode === ''}
                value={intervalTypeId}
                placeholder={
                  frequencyTypeCode === ''
                    ? t('admin.instrumentTypes.maintenanceModal.frequencySelectTypeFirst')
                    : t('admin.instrumentTypes.maintenanceModal.frequencySelectPlaceholder')
                }
                searchPlaceholder={t(
                  'admin.instrumentTypes.maintenanceModal.frequencySearchPlaceholder',
                )}
                emptyMessage={t('admin.instrumentTypes.maintenanceModal.frequencyEmpty')}
                options={[
                  {
                    value: NEW_INTERVAL_VALUE,
                    label: t('admin.instrumentTypes.maintenanceModal.frequencyNewOption'),
                  },
                  ...matchingIntervals.map((interval) => ({
                    value: String(interval.id),
                    label: formatIntervalOptionLabel(interval, matchingIntervals),
                  })),
                ]}
                // Picking a frequency that was saved with a Starts At fills this task's Starts At
                // from it (REBUILD-621); a frequency without one leaves the task's own value.
                // Opening New Frequency re-seeds Name to the type-matching auto placeholder.
                onChange={(value) => {
                  if (value === NEW_INTERVAL_VALUE) {
                    form.setData((previous) => ({
                      ...previous,
                      intervalTypeId: value,
                      newInterval: emptyNewInterval(previous.frequencyTypeCode),
                      // Brand-new frequency still defaults checked (legacy).
                      endsAtMidnight: true,
                    }))
                    return
                  }
                  const selectedInterval = intervalTypeOptions.find(
                    (interval) => String(interval.id) === value,
                  )
                  const nextStartTime = resolveStartTimeOnIntervalSelect({
                    frequencyTypeCode: form.data.frequencyTypeCode,
                    intervalDefaultStartTime: selectedInterval?.defaultStartTime,
                    currentStartTime: form.data.fixedScheduleStartTime,
                  })
                  const selection = resolveEndsAtMidnightOnIntervalSelect({
                    frequencyTypeCode: form.data.frequencyTypeCode,
                    fixedScheduleStartTime: nextStartTime,
                    intervalEndsAtMidnight: selectedInterval?.endsAtMidnight,
                    lateTimeIntervalHours: selectedInterval?.lateTimeIntervalHours,
                  })
                  form.setData((previous) => ({
                    ...previous,
                    intervalTypeId: value,
                    fixedScheduleStartTime: nextStartTime,
                    ...(selection.shouldApply ? { endsAtMidnight: selection.endsAtMidnight } : {}),
                  }))
                }}
              />
            </div>
          )}

          {selectedExistingInterval && (
            <div className="rounded-md border bg-muted/30 p-3 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">
                {t('admin.instrumentTypes.maintenanceModal.intervalOptionsTitle')}
              </p>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                {frequencyTypeCode === FREQUENCY_TYPE_TIMED &&
                  selectedExistingInterval.intervalHours != null && (
                    <>
                      <dt className="text-muted-foreground">
                        {t('admin.instrumentTypes.maintenanceModal.repeatsEveryLabel')}
                      </dt>
                      <dd>
                        {selectedExistingInterval.intervalHours}{' '}
                        {t('instruments.tasksSchedule.form.timeUnits.hours')}
                      </dd>
                    </>
                  )}
                {frequencyTypeCode === FREQUENCY_TYPE_TIMED &&
                  selectedExistingInterval.leadTimeIntervalHours != null && (
                    <>
                      <dt className="text-muted-foreground">
                        {t('admin.instrumentTypes.maintenanceModal.leadTimeLabel')}
                      </dt>
                      <dd>
                        {convertFromMinutes(
                          selectedExistingInterval.leadTimeIntervalHours,
                          selectedExistingInterval.leadTimeUnit ?? 'hours',
                        )}{' '}
                        {t(
                          `instruments.tasksSchedule.form.timeUnits.${selectedExistingInterval.leadTimeUnit ?? 'hours'}` as Parameters<
                            typeof t
                          >[0],
                        )}
                      </dd>
                    </>
                  )}
                {frequencyTypeCode === FREQUENCY_TYPE_CALENDAR_FIXED &&
                  selectedExistingInterval.frequencyRrule && (
                    <>
                      <dt className="text-muted-foreground">
                        {t('instruments.tasksSchedule.taskForm.pattern')}
                      </dt>
                      <dd>
                        {humanReadableStructuralRrule(selectedExistingInterval.frequencyRrule)}
                      </dd>
                    </>
                  )}
                {selectedExistingInterval.lateTimeIntervalHours != null && (
                  <>
                    <dt className="text-muted-foreground">
                      {t('instruments.tasksSchedule.taskForm.overdue')}
                    </dt>
                    <dd>
                      {convertFromMinutes(
                        selectedExistingInterval.lateTimeIntervalHours,
                        selectedExistingInterval.lateTimeUnit ?? 'hours',
                      )}{' '}
                      {t(
                        `instruments.tasksSchedule.form.timeUnits.${selectedExistingInterval.lateTimeUnit ?? 'hours'}` as Parameters<
                          typeof t
                        >[0],
                      )}
                    </dd>
                  </>
                )}
              </dl>
              {frequencyTypeCode === FREQUENCY_TYPE_CALENDAR_FIXED && (
                <div className="space-y-1.5 pt-1">
                  <Label htmlFor="maintenance-existing-starts-at">
                    {t('admin.instrumentTypes.maintenanceModal.startsAtLabel')} *
                  </Label>
                  <TenantTimeInput
                    id="maintenance-existing-starts-at"
                    value={form.data.fixedScheduleStartTime}
                    onChange={(nextValue) => form.setData('fixedScheduleStartTime', nextValue)}
                    aria-invalid={existingStartsAtMissing}
                    tenantTimeFormat={tenantTimeFormat}
                    allowEmpty
                  />
                  {/* Unlike a brand-new interval's fields, this reflects a pre-existing task —
                      show the reason Save is disabled immediately rather than only after blur,
                      so a missing value never disables Save with no visible explanation. */}
                  {existingStartsAtMissing && (
                    <p className="text-sm text-destructive">
                      {t('admin.instrumentTypes.maintenanceModal.startsAtRequired')}
                    </p>
                  )}
                </div>
              )}
              {showEndsAtMidnight && (
                <div className="space-y-1.5 pt-1">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="maintenance-ends-at-midnight-existing"
                      checked={form.data.endsAtMidnight}
                      onCheckedChange={(checked) =>
                        form.setData('endsAtMidnight', Boolean(checked))
                      }
                    />
                    <Label htmlFor="maintenance-ends-at-midnight-existing" className="font-normal">
                      {t('admin.instrumentTypes.maintenanceModal.endsAtMidnightLabel')}
                    </Label>
                  </div>
                  {!form.data.endsAtMidnight && (
                    <p className="text-sm text-amber-600">
                      {t('admin.instrumentTypes.maintenanceModal.endsAtMidnightWarning')}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {showIntervalOptions && (
            <div className="space-y-3 rounded-md border bg-muted/30 p-3">
              <h4 className="text-sm font-semibold">
                {t('admin.instrumentTypes.maintenanceModal.intervalOptionsTitle')}
              </h4>
              <div className="space-y-1.5">
                <Label>{t('admin.instrumentTypes.maintenanceModal.intervalNameLabel')} *</Label>
                <Input
                  value={newInterval.name}
                  onChange={(event) =>
                    form.setData('newInterval', {
                      ...form.data.newInterval,
                      name: event.target.value,
                    })
                  }
                  onBlur={() => markTouched('name')}
                  placeholder={t('admin.instrumentTypes.maintenanceModal.intervalNamePlaceholder')}
                  autoComplete="off"
                  maxLength={255}
                  aria-invalid={touched.name && (intervalNameMissing || intervalNameDuplicate)}
                />
                <p className="text-right text-xs text-muted-foreground">
                  {newInterval.name.length}/255
                </p>
                {touched.name && intervalNameMissing && (
                  <p className="text-sm text-destructive">
                    {t('admin.instrumentTypes.maintenanceModal.intervalNameRequired')}
                  </p>
                )}
                {touched.name && !intervalNameMissing && intervalNameDuplicate && (
                  <p className="text-sm text-destructive">
                    {t('admin.instrumentTypes.maintenanceModal.intervalNameDuplicate')}
                  </p>
                )}
                {intervalPreviewLabel && (
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">
                      {t('admin.instrumentTypes.maintenanceModal.intervalOptionPreview')}:{' '}
                    </span>
                    {intervalPreviewLabel}
                  </p>
                )}
              </div>

              {showLeadTime && (
                <div className="space-y-1.5">
                  <Label>{t('admin.instrumentTypes.maintenanceModal.repeatsEveryLabel')} *</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min={1}
                      value={newInterval.repeatsEveryValue}
                      onChange={(event) =>
                        setNewInterval({ repeatsEveryValue: event.target.value })
                      }
                      onBlur={() => markTouched('repeatsEveryValue')}
                      aria-invalid={touched.repeatsEveryValue && repeatsEveryMissing}
                      className="w-24"
                    />
                    <Select
                      value={newInterval.repeatsEveryUnit}
                      onValueChange={(value) => setNewInterval({ repeatsEveryUnit: value })}
                    >
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_UNITS.map((unit) => (
                          <SelectItem key={unit.value} value={unit.value}>
                            {t(unit.labelKey)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {touched.repeatsEveryValue && repeatsEveryMissing && (
                    <p className="text-sm text-destructive">
                      {t('admin.instrumentTypes.maintenanceModal.repeatsEveryRequired')}
                    </p>
                  )}
                  {timedNextExecution && (
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium">
                        {t('instruments.tasksSchedule.form.rrule.nextTriggerLabel')}:{' '}
                      </span>
                      {formatSiteDateTime(
                        toWallClockString(timedNextExecution, tenantTimeZone ?? 'UTC'),
                        tenantTimeZone ?? 'UTC',
                        tenantTimeFormat,
                      )}
                    </p>
                  )}
                  {timedOverdueDate && (
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium">
                        {t('instruments.tasksSchedule.form.rrule.overdueDateLabel')}:{' '}
                      </span>
                      {formatSiteDateTime(
                        toWallClockString(timedOverdueDate, tenantTimeZone ?? 'UTC'),
                        tenantTimeZone ?? 'UTC',
                        tenantTimeFormat,
                      )}
                    </p>
                  )}
                </div>
              )}

              {showStartsAt && (
                <>
                  <RruleBuilder
                    value={newInterval.frequencyRrule}
                    onChange={(rrule) => setNewInterval({ frequencyRrule: rrule })}
                    startsAt={newInterval.startsAt}
                    lateTimeMinutes={
                      newInterval.overdueValue !== ''
                        ? toMinutes(newInterval.overdueValue, newInterval.overdueUnit)
                        : undefined
                    }
                    endsAtMidnight={form.data.endsAtMidnight}
                    idPrefix="modal-rrule"
                  />

                  <div className="space-y-1.5">
                    <Label htmlFor="maintenance-new-starts-at">
                      {t('admin.instrumentTypes.maintenanceModal.startsAtLabel')} *
                    </Label>
                    <TenantTimeInput
                      id="maintenance-new-starts-at"
                      value={newInterval.startsAt}
                      onChange={(nextValue) => setNewInterval({ startsAt: nextValue })}
                      aria-invalid={startsAtMissing}
                      tenantTimeFormat={tenantTimeFormat}
                      allowEmpty
                    />
                    {/* Match existing-interval Starts At: show required as soon as value is empty
                        (clear can live-emit '' before blur; no touch gate). */}
                    {startsAtMissing && (
                      <p className="text-sm text-destructive">
                        {t('admin.instrumentTypes.maintenanceModal.startsAtRequired')}
                      </p>
                    )}
                  </div>
                </>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                {showLeadTime && (
                  <div className="space-y-1.5">
                    <Label>{t('admin.instrumentTypes.maintenanceModal.leadTimeLabel')}</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        min={0}
                        value={newInterval.leadValue}
                        onChange={(event) => {
                          const value = event.target.value
                          if (value === '' || Number(value) >= 0)
                            setNewInterval({ leadValue: value })
                        }}
                        className="w-24"
                      />
                      <Select
                        value={newInterval.leadUnit}
                        onValueChange={(value) => setNewInterval({ leadUnit: value })}
                      >
                        <SelectTrigger className="w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_UNITS.map((unit) => (
                            <SelectItem key={unit.value} value={unit.value}>
                              {t(unit.labelKey)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>{t('admin.instrumentTypes.maintenanceModal.overdueLabel')} *</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min={1}
                      value={newInterval.overdueValue}
                      onChange={(event) => {
                        const value = event.target.value
                        if (value === '' || Number(value) > 0)
                          setNewInterval({ overdueValue: value })
                      }}
                      onBlur={() => markTouched('overdueValue')}
                      aria-invalid={touched.overdueValue && overdueMissing}
                      className="w-24"
                    />
                    <Select
                      value={newInterval.overdueUnit}
                      onValueChange={(value) => setNewInterval({ overdueUnit: value })}
                    >
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_UNITS.map((unit) => (
                          <SelectItem key={unit.value} value={unit.value}>
                            {t(unit.labelKey)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {touched.overdueValue && overdueMissing && (
                    <p className="text-sm text-destructive">
                      {t('admin.instrumentTypes.maintenanceModal.overdueRequired')}
                    </p>
                  )}
                  {touched.overdueValue && overdueTooShort && (
                    <p className="text-sm text-destructive">
                      {t('admin.instrumentTypes.maintenanceModal.overdueMinimum')}
                    </p>
                  )}
                  {overdueTooLarge && (
                    <p className="text-sm text-destructive">
                      {t('admin.instrumentTypes.maintenanceModal.overdueMaximum', {
                        max: MAX_OVERDUE_VALUE,
                      })}
                    </p>
                  )}
                  {overdueDurationExceeded && (
                    <p className="text-sm text-destructive">
                      {t('admin.instrumentTypes.maintenanceModal.overdueDurationTooLong')}
                    </p>
                  )}
                </div>
              </div>

              {showEndsAtMidnight && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="maintenance-ends-at-midnight"
                      checked={form.data.endsAtMidnight}
                      onCheckedChange={(checked) =>
                        form.setData('endsAtMidnight', Boolean(checked))
                      }
                    />
                    <Label htmlFor="maintenance-ends-at-midnight" className="font-normal">
                      {t('admin.instrumentTypes.maintenanceModal.endsAtMidnightLabel')}
                    </Label>
                  </div>
                  {!form.data.endsAtMidnight && (
                    <p className="text-sm text-amber-600">
                      {t('admin.instrumentTypes.maintenanceModal.endsAtMidnightWarning')}
                    </p>
                  )}
                </div>
              )}

              {leadExceedsRepeats && (
                <p className="text-sm text-amber-600">
                  {t('admin.instrumentTypes.maintenanceModal.leadExceedsRepeatsWarning')}
                </p>
              )}
              {overdueExceedsRepeats && (
                <p className="text-sm text-amber-600">
                  {t('admin.instrumentTypes.maintenanceModal.overdueExceedsRepeatsWarning')}
                </p>
              )}
            </div>
          )}

          {!willBeScheduled && (
            <p className="text-sm text-muted-foreground">
              {t('admin.instrumentTypes.maintenanceModal.frequencyRequiredHint')}
            </p>
          )}
        </div>

        {/* Affects Compliance ------------------------------------------ */}
        <div className="flex items-center gap-2">
          <Checkbox
            id="maintenance-affects-compliance"
            checked={form.data.affectsCompliance}
            onCheckedChange={(checked) => form.setData('affectsCompliance', Boolean(checked))}
          />
          <Label htmlFor="maintenance-affects-compliance" className="font-normal cursor-pointer">
            {t('admin.instrumentTypes.maintenanceModal.affectsCompliance')}
          </Label>
        </div>

        {/* Controls/Reagents links + uses lots ------------------------- */}
        {/* The single linked lot (Task Links, lot_id) and the multi used-lots set
            (Task Uses Lots, instrument_task_used_lots) are distinct concepts and
            round-trip independently (E27-S21). */}
        <div className="space-y-4 rounded-md border p-3">
          <div className="space-y-1.5">
            <Label>{t('admin.instrumentTypes.maintenanceModal.taskLinksLabel')}</Label>
            <p className="text-xs text-muted-foreground">
              {t('admin.instrumentTypes.maintenanceModal.taskLinksNote')}
            </p>
            {lotOptions.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {t('admin.instrumentTypes.maintenanceModal.taskUsesLotsEmpty')}
              </p>
            ) : (
              <Select
                value={form.data.lotId || LOT_LINK_NONE_VALUE}
                onValueChange={(value) => {
                  const newLotId = value === LOT_LINK_NONE_VALUE ? '' : value
                  form.setData((previous) => ({
                    ...previous,
                    lotId: newLotId,
                    usedLotIds: newLotId
                      ? previous.usedLotIds.filter((id) => id !== Number(newLotId))
                      : previous.usedLotIds,
                  }))
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={LOT_LINK_NONE_VALUE}>
                    {t('admin.instrumentTypes.maintenanceModal.taskLinksNone')}
                  </SelectItem>
                  {linkFilteredLotOptions.map((lot) => (
                    <SelectItem key={lot.id} value={String(lot.id)}>
                      {lot.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>{t('admin.instrumentTypes.maintenanceModal.taskUsesLotsLabel')}</Label>
            <p className="text-xs text-muted-foreground">
              {t('admin.instrumentTypes.maintenanceModal.taskUsesLotsNote')}
            </p>
            {lotOptions.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {t('admin.instrumentTypes.maintenanceModal.taskUsesLotsEmpty')}
              </p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {usesFilteredLotOptions.map((lot) => {
                  const checked = form.data.usedLotIds.includes(lot.id)
                  return (
                    <label key={lot.id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(value) =>
                          form.setData(
                            'usedLotIds',
                            value === true
                              ? [...form.data.usedLotIds, lot.id]
                              : form.data.usedLotIds.filter((id) => id !== lot.id),
                          )
                        }
                      />
                      {lot.label}
                    </label>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Data fields ------------------------------------------------- */}
        <div className="space-y-3 rounded-md border p-3">
          <h4 className="text-sm font-semibold">
            {t('admin.instrumentTypes.maintenanceModal.dataFieldsTitle')}
          </h4>

          {form.data.dataFields.map((field, fieldIndex) => {
            // Keep original indices so remove/clone target the correct row; inactive
            // (soft-removed) fields stay in form state for submit but are hidden like legacy.
            if (!field.active) return null

            const fieldTypeCode = resolveFieldTypeCode(
              field.instrumentTaskDataFieldTypeId,
              dataFieldTypeOptions,
            )
            const fieldValidationOptions = validationOptionsForField(
              fieldTypeCode,
              dataFieldValidationTypeOptions,
            )
            const typeAndFlagsLabels: DataFieldTypeAndFlagsLabels = {
              fieldTypeLabel: t('admin.instrumentTypes.maintenanceModal.fieldTypeLabel'),
              fieldTypePlaceholder: t(
                'admin.instrumentTypes.maintenanceModal.fieldTypePlaceholder',
              ),
              fieldTypeRequired: t('admin.instrumentTypes.maintenanceModal.fieldTypeRequired'),
              required: t('admin.instrumentTypes.maintenanceModal.required'),
              isConfidential: t('admin.instrumentTypes.maintenanceModal.isConfidential'),
              validateFieldTitle: t('admin.instrumentTypes.maintenanceModal.validateFieldTitle'),
              acceptableValuesFromLot: t(
                'admin.instrumentTypes.maintenanceModal.acceptableValuesFromLot',
              ),
            }
            const validationEditorLabels: DataFieldValidationEditorLabels = {
              acceptableValuePlaceholder: t(
                'admin.instrumentTypes.maintenanceModal.validateFieldValue',
              ),
              acceptableBetweenMin: t(
                'admin.instrumentTypes.maintenanceModal.validateFieldBetweenMin',
              ),
              acceptableBetweenMax: t(
                'admin.instrumentTypes.maintenanceModal.validateFieldBetweenMax',
              ),
              acceptableValidationType: t(
                'admin.instrumentTypes.maintenanceModal.validateFieldValidationType',
              ),
              acceptableValidationTypePlaceholder: t(
                'admin.instrumentTypes.maintenanceModal.validateFieldValidationType',
              ),
              acceptableIfLabel: t(
                'admin.instrumentTypes.maintenanceModal.validateFieldAcceptableIf',
              ),
              acceptableValidationHeader: t(
                'admin.instrumentTypes.maintenanceModal.validateFieldAcceptableIf',
              ),
              betweenInvalid: t(
                'admin.instrumentTypes.maintenanceModal.validateFieldBetweenInvalid',
              ),
              contradictoryConditions: t(
                'admin.instrumentTypes.maintenanceModal.validateFieldContradictory',
              ),
              acceptableIsAcceptable: t(
                'admin.instrumentTypes.maintenanceModal.validateFieldIsAcceptable',
              ),
              acceptableAddRow: t('admin.instrumentTypes.maintenanceModal.validateFieldAddRow'),
              operatorTypeOr: t('admin.instrumentTypes.maintenanceModal.validateFieldOperatorOr'),
              operatorTypeAnd: t('admin.instrumentTypes.maintenanceModal.validateFieldOperatorAnd'),
              acceptableRemoveRow: t(
                'admin.instrumentTypes.maintenanceModal.validateFieldRemoveRow',
              ),
              definedValuesTitle: undefined,
              definedValuePlaceholder: t(
                'admin.instrumentTypes.maintenanceModal.definedValuePlaceholder',
              ),
              definedIsAcceptable: t('admin.instrumentTypes.maintenanceModal.definedIsAcceptable'),
              definedAddRow: t('admin.instrumentTypes.maintenanceModal.definedValueAddRow'),
              definedRemove: t('admin.instrumentTypes.maintenanceModal.definedValueRemove'),
              definedAcceptableRequired: t(
                'admin.instrumentTypes.maintenanceModal.definedAcceptableRequired',
              ),
            }
            const isDefinedType = isDefinedValueType(fieldTypeCode)
            const typeAndFlagsProps = {
              instrumentTaskDataFieldTypeId: field.instrumentTaskDataFieldTypeId,
              typeOptions: dataFieldTypeOptions,
              fieldTypeCode,
              required: field.required,
              requiresValidation: field.requiresValidation,
              isConfidential: field.isConfidential,
              onTypeChange: (typeId: string) =>
                patchDataField(
                  fieldIndex,
                  applyDataFieldTypeDefaults(
                    typeId,
                    dataFieldValidationTypeOptions,
                    dataFieldTypeOptions,
                  ),
                ),
              onRequiredChange: (required: boolean) => patchDataField(fieldIndex, { required }),
              onRequiresValidationChange: (requiresValidation: boolean) =>
                patchDataField(fieldIndex, { requiresValidation }),
              onIsConfidentialChange: (isConfidential: boolean) =>
                patchDataField(fieldIndex, { isConfidential }),
              labels: typeAndFlagsLabels,
              idPrefix: `maintenance-field-${fieldIndex}`,
              checkboxLayout: 'inline' as const,
              showRequiresValidation: true,
              validateFieldDisabled: shouldDisableValidateField(fieldTypeCode),
              typeRequired: true,
              typeInvalid: submitAttempted && dataFieldTypeMissing(field),
              lotLinked: linkedLotId != null,
            }

            return (
              <div key={fieldIndex} className="space-y-3 rounded-md border bg-muted/20 p-3">
                {/* Required + Is Confidential — field-level flags, shown at top like legacy */}
                <div className="border-b pb-2">
                  <DataFieldTypeAndFlags
                    {...typeAndFlagsProps}
                    showTypeSelect={false}
                    showValidateCheckbox={false}
                  />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label>
                        {t('admin.instrumentTypes.maintenanceModal.fieldLabelLabel')}
                        <span className="ml-0.5 text-destructive">*</span>
                      </Label>
                      <Input
                        value={field.label}
                        onChange={(event) =>
                          patchDataField(fieldIndex, { label: event.target.value })
                        }
                        placeholder={t(
                          'admin.instrumentTypes.maintenanceModal.fieldLabelPlaceholder',
                        )}
                        autoComplete="off"
                        maxLength={DATA_FIELD_TEXT_MAX_LENGTH}
                        aria-invalid={submitAttempted && dataFieldLabelMissing(field)}
                      />
                      <p className="text-right text-xs text-muted-foreground">
                        {field.label.length}/{DATA_FIELD_TEXT_MAX_LENGTH}
                      </p>
                      {submitAttempted && dataFieldLabelMissing(field) && (
                        <p className="text-sm text-destructive">
                          {t('admin.instrumentTypes.maintenanceModal.fieldLabelRequired')}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label>
                        {t('admin.instrumentTypes.maintenanceModal.fieldDescriptionLabel')}
                      </Label>
                      <Input
                        value={field.dataFieldDescription}
                        onChange={(event) =>
                          patchDataField(fieldIndex, { dataFieldDescription: event.target.value })
                        }
                        placeholder={t(
                          'admin.instrumentTypes.maintenanceModal.fieldDescriptionPlaceholder',
                        )}
                        autoComplete="off"
                        maxLength={200}
                      />
                      <p className="text-right text-xs text-muted-foreground">
                        {field.dataFieldDescription.length}/200
                      </p>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <DataFieldTypeAndFlags {...typeAndFlagsProps} showFlags={false} />
                  </div>
                </div>

                {/* Validate Field — validation-level flag, shown before validation editor */}
                <DataFieldTypeAndFlags
                  {...typeAndFlagsProps}
                  showTypeSelect={false}
                  showFieldCheckboxes={false}
                />

                {(linkedLotId == null || isDefinedType) &&
                  (isDefinedType ||
                    (shouldShowValidateField(fieldTypeCode) && field.requiresValidation)) && (
                    <DataFieldValidationEditor
                      fieldTypeCode={fieldTypeCode}
                      validationTypeOptions={fieldValidationOptions}
                      acceptableValues={field.acceptableValues}
                      definedValues={field.definedValues}
                      onPatchAcceptable={(rowIndex, patch) =>
                        patchAcceptable(fieldIndex, rowIndex, patch)
                      }
                      onAddAcceptable={() => addAcceptable(fieldIndex)}
                      onRemoveAcceptable={(rowIndex) => removeAcceptable(fieldIndex, rowIndex)}
                      onPatchDefined={(rowIndex, value) =>
                        patchDefined(fieldIndex, rowIndex, value)
                      }
                      onToggleDefinedAcceptable={(rowIndex, isAcceptable) =>
                        toggleDefinedAcceptable(fieldIndex, rowIndex, isAcceptable)
                      }
                      onAddDefined={() => addDefined(fieldIndex)}
                      onRemoveDefined={(rowIndex) => removeDefined(fieldIndex, rowIndex)}
                      labels={validationEditorLabels}
                      variant="inline"
                      showAcceptableSection={!isDefinedType && linkedLotId == null}
                      showValidationHeader={false}
                      showDefinedCharCount={isDefinedType}
                      showDefinedAcceptable={linkedLotId == null}
                      showValidationErrors={submitAttempted}
                      idPrefix={`maintenance-field-${fieldIndex}`}
                    />
                  )}

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => cloneDataField(fieldIndex)}
                  >
                    {t('admin.instrumentTypes.maintenanceModal.cloneDataField')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() => removeDataField(fieldIndex)}
                  >
                    {t('admin.instrumentTypes.maintenanceModal.removeDataField')}
                  </Button>
                </div>
              </div>
            )
          })}

          <Button type="button" variant="outline" size="sm" onClick={addDataField}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            {t('admin.instrumentTypes.maintenanceModal.addDataField')}
          </Button>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('admin.instrumentTypes.maintenanceModal.cancel')}
        </Button>
        <Button type="button" onClick={submit} disabled={submitDisabled}>
          {t('admin.instrumentTypes.maintenanceModal.save')}
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}

export const MaintenanceTaskModal = ({
  open,
  onOpenChange,
  typeId,
  editingRow,
  cloneFromRow,
  frequencyTypeOptions,
  intervalTypeOptions,
  dataFieldTypeOptions,
  dataFieldValidationTypeOptions,
  lotOptions,
  draftMode = false,
  onLocalSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  typeId: number | null
  editingRow: WebAdminInstrumentTypeMaintenanceTaskRow | null
  cloneFromRow: WebAdminInstrumentTypeMaintenanceTaskRow | null
  frequencyTypeOptions: WebFrequencyType[]
  intervalTypeOptions: WebInstrumentTaskIntervalType[]
  dataFieldTypeOptions: WebInstrumentsTaskDataFieldTypeOption[]
  dataFieldValidationTypeOptions: WebInstrumentsTaskDataFieldValidationTypeOption[]
  lotOptions: LotOption[]
  draftMode?: boolean
  onLocalSave?: (
    payload: MaintenanceTaskPersistPayload,
    displayRow: WebAdminInstrumentTypeMaintenanceTaskRow,
  ) => void
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    {open && (
      <MaintenanceTaskModalForm
        key={
          editingRow ? `edit-${editingRow.id}` : cloneFromRow ? `clone-${cloneFromRow.id}` : 'new'
        }
        typeId={typeId}
        editingRow={editingRow}
        cloneFromRow={cloneFromRow}
        frequencyTypeOptions={frequencyTypeOptions}
        intervalTypeOptions={intervalTypeOptions}
        dataFieldTypeOptions={dataFieldTypeOptions}
        dataFieldValidationTypeOptions={dataFieldValidationTypeOptions}
        lotOptions={lotOptions}
        onClose={() => onOpenChange(false)}
        draftMode={draftMode}
        onLocalSave={onLocalSave}
      />
    )}
  </Dialog>
)

export default MaintenanceTaskModal
